import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import {
  CompleteTransaction, BasicHeader, ErrorPage, WAIT_ID,
  V1_V2X_TRANSACTION_COMPLETION, V1_CHALLENGE_PROCESS,
  TECHNICAL_ISSUES_ID,
} from '@americanexpress/gravity-core';
import { connect } from 'react-redux';
import GravityHeader from './GravityHeader';
import GravityFooter from './GravityFooter';
import reducer from '../ducks';
import { fetchProcessPayAuth } from '../config/config';

const footerHideRoutes = ['/pageWrapper', '/red', '/rewards', '/manageExpress', '/'];

/* eslint complexity: ["error", 30] */ /* istanbul ignore file */
export const GravityPageWrapper = (props) => {
  /* eslint-disable-next-line react/destructuring-assignment */
  const isDemoMode = props.queryParams.props;
  let demoResponse = null;
  if (isDemoMode) {
    /* eslint-disable-next-line react/destructuring-assignment */
    demoResponse = props.queryParams.props.flow;
  }

  const {
    router,
    gemUrl,
    logger,
    apiUrl,
    queryParams,
    updateSpinner,
    reportError,
    currentScreenId,
    location,
    updateShowDialog,
    showDialog,
    updateStaticMessage,
    changeLang,
    updateSettingsPayloadByType,
    settingsClicked,
    settingsData,
    children,
    updateFooter,
    updateAbandonData,
    updateScreenId,
    incomingReq,
    updateCompletion,
    updateDataHeader,
    dataHeader,
    prevAcsTransId,
    updatePrevAcsTransId,
    updateFlowData,
    /* eslint-disable-next-line react/prop-types */
    stateValue,
  } = props;
  const [completeTransaction, setCompleteTransaction] = useState(false);
  const [completeStatus, setCompleteStatus] = useState('');
  const [flowFetchCount, setFlowFetchCount] = useState(1);
  const [flow, setFlow] = useState({});
  const [loadStatus, setLoadStatus] = useState('loading');
  const [loadErrors, setLoadErrors] = useState(null);

  /* istanbul ignore next */
  useEffect(() => {
    logger({
      name: 'GRAVITY_PAGE_WRAPPER_LOADED',
      acsTransId: (incomingReq || {}).acsTransId,
      url: router.location.search,
    });
  }, []);

  const fetchFlow = async (isRetry) => {
    if (isRetry) {
      setLoadStatus('loading');
    }
    const { url, opts } = fetchProcessPayAuth(stateValue);

    logger({
      url,
      name: 'INITIALIZE_PAY_AUTH_CALL',
      acsTransId: (incomingReq || {}).acsTransId,
    });

    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), 10000);
    const { signal } = abortController;

    fetch(url, { ...opts, signal })
      .then(async (res) => {
        const data = res.headers.get('data');
        if (data) updateDataHeader(data);

        const contentType = res.headers.get('Content-Type');
        const isJson = String(contentType).slice(0, 'application/json'.length) === 'application/json';
        const body = await res[isJson ? 'json' : 'text']();
        const { status } = res;

        logger({
          name: 'PAY_AUTH_RESPONSE_STATUS_CODE',
          status,
          acsTransId: queryParams.acsTransId,
        });

        return res.ok
          ? Promise.resolve(body)
          : Promise.reject(
            Object.assign(new Error(`${res.statusText} (${res.url})`), {
              body,
              status,
            })
          );
      })
      .then((response) => {
        logger({
          name: 'PAY_AUTH_CALL_SUCCESSFUL',
          acsTransId: (incomingReq || {}).acsTransId,
          response,
        });

        updateFlowData({
          ...response,
          headers: { data: { id: 'flow', opts, actionType: 'POST' } },
        });
        setFlow({
          ...response,
          headers: { data: { id: 'flow', opts, actionType: 'POST' } },
        });

        logger({
          name: 'STORE_UPDATED_WITH_PROCESS_RESPONSE',
          acsTransId: (incomingReq || {}).acsTransId,
        });

        setLoadStatus('complete');
      })
      .catch((error) => {
        const errors = Object.values(error || {});
        const errorLog = errors.length === 0
          ? { status: 'processPayAuth call failed', message: 'Failed to fetch' }
          : {
            status: (errors[0] || '').includes('signal is aborted') ? 'The request timed out after 10000ms' : errors[0],
            message: errors[1],
          };
        clearTimeout(timer);
        logger(
          {
            url,
            name: 'PAY_AUTH_CALL_FAILED',
            acsTransId: (incomingReq || {}).acsTransId,
            errorLog,
          },
          errorLog
        );

        setLoadErrors(errorLog);
        setLoadStatus('complete');
      });
  };

  useEffect(() => {
    if (isDemoMode) {
      setFlow(demoResponse);
      setLoadStatus('complete');
      return;
    }

    if (queryParams.acsTransId) fetchFlow();
  }, [queryParams.acsTransId, isDemoMode]);

  const handleTimeouts = (timeout) => {
    setTimeout(() => {
      if (showDialog) {
        updateShowDialog(false);
      }
      updateSpinner(true);
      updateFooter(false);
      setCompleteStatus('sessionTimeout');
      setCompleteTransaction(true);
    }, timeout);
  };

  const handleRouting = () => {
    switch (flow.flow) {
      case 'G':
        if (flow.stepUpMethod === 'OTC_LITE') {
          router.replace('/otc');
          break;
        } else if (flow.stepUpMethod === 'BLOCKED') {
          updateStaticMessage('blocked');
          router.replace('/red');
          break;
        } else if (flow.stepUpMethod === 'LOCKED_CONSUMER') {
          updateStaticMessage('locked_consumer');
          router.replace('/red');
          break;
        } else if (flow.eligibilityIndicators.elUplifted && flow.eligibilityIndicators.pwpEnabled) {
          router.replace('/rewards');
          break;
        }
        router.replace('/express');
        break;
      case 'R':
        router.replace('/red');
        break;
      case 'Y':
        switch (flow.stepUpMethod) {
          case 'CSC':
            router.replace('/csc');
            break;
          case 'OTC':
            router.replace('/otc');
            break;
          case 'OTC_PIN':
            router.replace('/otc');
            break;
          case 'PIN':
            router.replace('/pin');
            break;
          case 'MANAGE_EL':
            router.replace('/manageExpress');
            break;
          case 'EMI':
            router.replace('/emi');
            break;
          case 'PWP':
            router.replace('/rewards');
            break;
          case 'UCD':
            router.replace('/ucd');
            break;
          case 'PUSH':
            router.replace('/push');
            break;
          default: router.replace('/red');
        }
        break;
      default:
        logger(
          {
            statusCode: flow.flow,
            acsTransId: queryParams.acsTransId,
            error: `Invalid flow color returned: ${flow.flow}`,
          },
          flow.flow
        );
        router.replace('/red');
    }
  };

  useEffect(() => {
    const params = router && router.location && router.location.search
    && new URLSearchParams(router.location.search);
    if (queryParams.acsTransId || (params && !params.get('props'))) {
      updateShowDialog(true);
    }
    const disable = (event) => {
      event.preventDefault();
    };
    window.addEventListener('contextmenu', disable);

    return () => {
      window.removeEventListener('contextmenu', disable);
    };
  }, []);

  const updateTimeouts = () => {
    const { acsTransId, token } = queryParams;
    if (flow.timeouts && flow.timeouts.session) {
      handleTimeouts(flow.timeouts.session);
    } else if (acsTransId && token) {
      handleTimeouts(1800000);
    }
  };
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (loadStatus === 'complete' && (flow.flow || flow.statusCode || loadErrors)) {
      if (flowFetchCount === 1 && !loadErrors && (prevAcsTransId === flow.acsTransId)) {
        if (flowFetchCount <= 6) {
          logger({
            apiUrl,
            name: 'STORE_NOT_CLEARED_PAYAUTH_GET_REATTEMPT',
            acsTransId: (incomingReq || {}).acsTransId,
          });

          const delay = flowFetchCount === 1 ? 200 : 500;
          setTimeout(() => {
            setFlowFetchCount(flowFetchCount + 1);
            fetchFlow(true);
          }, delay);
        } else {
          logger({
            apiUrl,
            name: 'STORE_NOT_CLEARED_PAYAUTH_GET_REATTEMPT_FAILURE',
            acsTransId: incomingReq.acsTransId,
          });
          router.replace('/red');
        }
      } else {
        if (!loadErrors) {
          if (flow.acsTransId) updatePrevAcsTransId(flow.acsTransId);
        }

        if (flowFetchCount > 1 && !loadErrors) {
          logger({
            apiUrl,
            name: 'PAYAUTH_GET_REATTEMPT_SUCCESS',
            acsTransId: incomingReq.acsTransId,
          });
        }

        if (loadErrors && !flow.flow) {
          const log = {
            status: loadErrors.status,
            acsTransId: (queryParams && queryParams.acsTransId) || 'missing acsTransId',
            error: loadErrors.message === 'Failed to fetch' ? 'Failed to fetch - Not able to reach the ProcessPayAuth api due to domain not reachable' : loadErrors.message,
            apiUrl: `${apiUrl}${V1_CHALLENGE_PROCESS}`,
          };
          if (flowFetchCount > 1 && flowFetchCount <= 6) log.name = 'PAYAUTH_GET_REATTEMPT_FAILURE';
          logger(log, loadErrors);

          if ((loadErrors.message === 'Failed to fetch' || (loadErrors.status || '').includes('The request timed out')) && flowFetchCount < 6) {
            logger({
              apiUrl,
              name: 'PAYAUTH_GET_REATTEMPT',
              acsTransId: incomingReq.acsTransId,
            });

            const delay = flowFetchCount === 1 ? 200 : 500;
            setLoadErrors(null);
            setFlowFetchCount(flowFetchCount + 1);
            setTimeout(() => {
              fetchFlow(true);
            }, delay);
          } else {
            const { acsTransId, token, locale } = queryParams;
            if (acsTransId && token && locale) {
              router.replace('/red');
            } else {
              if (acsTransId && token) updateScreenId(TECHNICAL_ISSUES_ID);
              updateSpinner(false);
            }
          }
        } else if (flow.statusCode === 'NCD') {
          router.replace('/ncd');
        } else if (flow.statusCode === 'NCD_CSC') {
          router.replace('/csc');
        } else if (['COMPLETE_TRANSACTION', 'INELIGIBLE_CARD'].includes(flow.statusCode)) {
          if (showDialog) {
            updateShowDialog(false);
          }
          logger(
            {
              statusCode: flow.flow,
              acsTransId: queryParams.acsTransId,
              error: `statusCode returned as: ${flow.statusCode}`,
            },
            flow.statusCode
          );
          updateSpinner(true);
          updateFooter(false);
          setCompleteStatus(flow.statusCode === 'INELIGIBLE_CARD' ? 'ineligible' : 'complete');
          setCompleteTransaction(true);
        } else if (flow.statusCode === 'CARD_LOCKED') {
          updateStaticMessage('locked');
          router.replace('/red');
        } else if (flow.statusCode === 'AUTH_CODE_AUTO_FAILED') {
          updateStaticMessage('auto_fail');
          router.replace('/red');
        } else if (!flow.flow || flow.statusCode === 'TECHNICAL_ISSUE') {
          const log = {
            statusCode: flow.statusCode,
            acsTransId: queryParams.acsTransId,
          };
          logger(log, flow.statusCode);
          router.replace('/red');
        } else {
          handleRouting();
        }
      }
      updateTimeouts();
    }
  }, [loadStatus, loadErrors, flow]);

  useEffect(() => {
    const requiredData = apiUrl && queryParams
    && queryParams.acsTransId && queryParams.token
    && currentScreenId;
    if (requiredData) {
      const postUrl = `${apiUrl}${V1_V2X_TRANSACTION_COMPLETION}${
        queryParams.mockEnabled === 'Y' ? '/mock' : ''
      }`;
      updateAbandonData({
        postUrl, queryParams, currentScreenId, incomingReq,
      });
    }
  }, [queryParams.acsTransId, queryParams.token, currentScreenId]);

  if (completeTransaction) {
    return (
      <CompleteTransaction
        screenId={currentScreenId || WAIT_ID || 'wait_screen'}
        eventCode={completeStatus}
        processPayAuthApi={apiUrl}
        queryParams={queryParams}
        incomingReq={incomingReq}
        updateCompletion={updateCompletion}
      />
    );
  }

  const { acsTransId, token, locale } = queryParams;
  const requiredParams = acsTransId && token && locale;
  if (loadErrors && !requiredParams) {
    return <ErrorPage showHeader={true} />;
  }

  if (loadStatus === 'loading') {
    return null;
  }

  const hideSettings = location.pathname !== '/red';
  const showHeader = flow.statusCode !== 'COMPLETE_TRANSACTION';
  const payAuthSuccess = flow.statusCode && flow.statusCode !== 'SYSTEM_ERROR';
  return (
    <Fragment>
      {showHeader && (
        payAuthSuccess ? (
          <GravityHeader
            flow={flow}
            hideSettings={hideSettings}
            router={router}
            gemUrl={gemUrl}
            updateSpinner={updateSpinner}
            changeLang={changeLang}
            updateSettingsPayloadByType={updateSettingsPayloadByType}
            queryParams={queryParams}
            reportError={reportError}
            currentScreenId={currentScreenId}
            processPayAuthApi={apiUrl}
            logger={logger}
            updateDataHeader={updateDataHeader}
            dataHeader={dataHeader}
          />
        ) : (
          <BasicHeader />
        )
      )}
      {children}
      { /* istanbul ignore next */ !footerHideRoutes.includes(window.location.pathname)
      && (
        <GravityFooter
          flow={flow}
          router={router}
          updateSpinner={updateSpinner}
          currentScreenId={currentScreenId}
          queryParams={queryParams}
          processPayAuthApi={apiUrl}
          settingsData={settingsData}
          settingsClicked={settingsClicked}
          changeLang={changeLang}
          showDialog={showDialog}
          updateShowDialog={updateShowDialog}
          gemUrl={gemUrl}
        />
      )}
    </Fragment>
  );
};

GravityPageWrapper.holocron = {
  name: 'axp-gravity-page-wrapper',
  reducer,
};

GravityPageWrapper.propTypes = {
  gemUrl: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  apiUrl: PropTypes.string.isRequired,
  queryParams: PropTypes.shape({
    acsTransId: PropTypes.string,
    mockEnabled: PropTypes.string,
    token: PropTypes.string,
    locale: PropTypes.string,
    props: PropTypes.string,
  }).isRequired,
  updateSpinner: PropTypes.func.isRequired,
  updateStaticMessage: PropTypes.func.isRequired,
  currentScreenId: PropTypes.string,
  updateShowDialog: PropTypes.func.isRequired,
  showDialog: PropTypes.bool.isRequired,
  logger: PropTypes.func.isRequired,
  changeLang: PropTypes.string.isRequired,
  updateSettingsPayloadByType: PropTypes.func.isRequired,
  reportError: PropTypes.func.isRequired,
  settingsClicked: PropTypes.bool.isRequired,
  settingsData: PropTypes.string.isRequired,
  updateFooter: PropTypes.func.isRequired,
  updateAbandonData: PropTypes.func.isRequired,
  updateScreenId: PropTypes.func.isRequired,
  incomingReq: PropTypes.shape({
    acsTransId: PropTypes.string.isRequired,
  }).isRequired,
  updateCompletion: PropTypes.func.isRequired,
  updateDataHeader: PropTypes.func.isRequired,
  dataHeader: PropTypes.string.isRequired,
  prevAcsTransId: PropTypes.string,
  updatePrevAcsTransId: PropTypes.func.isRequired,
  updateFlowData: PropTypes.func.isRequired,
};

GravityPageWrapper.defaultProps = {
  currentScreenId: 'page_wrapper',
  prevAcsTransId: '',
};

export const mapStateToProps = (state) => ({
  queryParams: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'acsTransId']),
  apiUrl: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'processPayAuthApi'])
  || state.getIn(['config', 'gravityAuthnUrl']),
  gemUrl: state.getIn(['config', 'gemUrl']),
  currentScreenId: state.getIn(['modules', 'axp-gravity-page-wrapper', 'currentScreenId']),
  changeLang: state.getIn(['modules', 'axp-gravity-page-wrapper', 'currentLanguage']),
  abandonData: state.getIn(['modules', 'axp-gravity-page-wrapper', 'abandonData']),
  showDialog: state.getIn(['modules', 'axp-gravity-page-wrapper', 'showDialog']),
  settingsData: state.getIn(['modules', 'axp-gravity-page-wrapper', 'settingsData']),
  settingsClicked: state.getIn(['modules', 'axp-gravity-page-wrapper', 'settingsClicked']),
  incomingReq: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'incomingReq']) || {},
  dataHeader: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'dataHeader']),
  prevAcsTransId: state.getIn(['modules', 'axp-gravity-root', 'rootReducer', 'prevAcsTransId']),
  stateValue: state,
});

export const mapDispatchToProps = (dispatch) => ({
  updateSpinner: (payload) => dispatch({ type: 'modules/axp-gravity-root/SETSPINNER', payload }),
  updateCompletion: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_COMPLETION', payload }),
  updateShowDialog: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_SHOW_DIALOG', payload }),
  updateFooter: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_FOOTER', payload }),
  updateStaticMessage: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_STATIC_MESSAGE', payload }),
  updateSettingsPayloadByType: (type, payload) => dispatch({ type, payload }),
  updateAbandonData: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_ABANDON_DATA', payload }),
  updateScreenId: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_SCREEN_ID', payload }),
  updateDataHeader: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_DATA_HEADER', payload }),
  updatePrevAcsTransId: (payload) => dispatch({ type: 'modules/axp-gravity-root/UPDATE_PREV_ACS_TRANS_ID', payload }),
  updateFlowData: (payload) => dispatch({ type: 'modules/axp-gravity-page-wrapper/UPDATE_FLOW_DATA', payload }),
  reportError: (
    error, moduleID, acsTransId
  ) => dispatch(addErrorToReport(
    {
      message: acsTransId || 'ONEAPP_LOG',
      eventName: 'ERROR',
    },
    {
      collectionMethod: 'componentDidCatch',
      moduleID,
      acsTransId,
      error: JSON.stringify(error),
      eventType: 'ERROR',
      transactionId: acsTransId,
    }
  )),
  logger: (
    log, error
  ) => dispatch(addErrorToReport(
    {
      message: (log || {}).acsTransId || 'ONEAPP_LOG',
      eventName: (log || {}).name || 'ERROR',
    },
    {
      collectionMethod: 'loadErrors',
      moduleID: 'axp-gravity-page-wrapper',
      logDetails: JSON.stringify(log),
      error: JSON.stringify(error),
      eventType: (log || {}).name || 'ERROR',
      transactionId: (log || {}).acsTransId,
    }
  )),
});

const hocChain = compose(
  connect(mapStateToProps, mapDispatchToProps)
);

export default hocChain(GravityPageWrapper);
